// Deterministic workload generation. Given the same seed, the generator
// produces the same op stream — so a demo (or a bug report) is reproducible.

import { Rng, Zipf } from "./rng";

export type Op =
  | { op: "put"; key: string; value: string }
  | { op: "get"; key: string }
  | { op: "delete"; key: string };

export interface WorkloadOptions {
  keySpace: number;
  valueLen: number;
}

const DEFAULTS: WorkloadOptions = { keySpace: 2000, valueLen: 32 };

export function key(i: number): string {
  return "user" + String(i).padStart(6, "0");
}

export class Workload {
  private rng: Rng;
  private zipf: Zipf;
  private seqCounter = 0;
  readonly opts: WorkloadOptions;

  constructor(
    readonly seed: number,
    opts: Partial<WorkloadOptions> = {},
  ) {
    this.opts = { ...DEFAULTS, ...opts };
    this.rng = new Rng(seed);
    this.zipf = new Zipf(this.opts.keySpace, this.rng);
  }

  private value(): string {
    // Deterministic pseudo-payload of the configured length.
    let s = "";
    while (s.length < this.opts.valueLen) {
      s += this.rng.next().toString(36).slice(2);
    }
    return s.slice(0, this.opts.valueLen);
  }

  sequentialPut(): Op {
    const k = key(this.seqCounter++ % this.opts.keySpace);
    return { op: "put", key: k, value: this.value() };
  }

  randomPut(): Op {
    return {
      op: "put",
      key: key(this.rng.int(this.opts.keySpace)),
      value: this.value(),
    };
  }

  zipfianGet(): Op {
    return { op: "get", key: key(this.zipf.next()) };
  }

  randomDelete(): Op {
    return { op: "delete", key: key(this.rng.int(this.opts.keySpace)) };
  }

  /** Mixed batch used by the write-burst button and the verifier. */
  mixed(n: number, putW = 0.6, getW = 0.3): Op[] {
    const ops: Op[] = [];
    for (let i = 0; i < n; i++) {
      const r = this.rng.next();
      if (r < putW) ops.push(this.randomPut());
      else if (r < putW + getW) ops.push(this.zipfianGet());
      else ops.push(this.randomDelete());
    }
    return ops;
  }
}
